import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { TagChips, TagPills } from '../../components/common/EventTags';
import { QuestionResult } from '../../components/common/SurveyResults';
import { apiRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './HostEvent.css';

/* The company's question library — write it once, ask it wherever, read it all together.

   A LIBRARY first and an event feature second. What gets reused is the question SET; an event is
   a use of one. So this screen is built to be narrowed rather than browsed: search the questions
   themselves ("did we ever ask about the commute"), narrow by tag, or by whether a set is reused,
   one-off or not in use yet. Picking one up happens on the event — the host screen's "add
   survey" is this same library minus what tonight already asks.

   ★ A survey owns itself (``Survey.owner_company_id``). It used to have no owner column at
   all: whose it was got DERIVED from the events it ran on, which is why one had to be born
   attached to an event and why the last link could not be removed. Both of those are gone. An
   unused question set is an ordinary thing for a library to hold.

   ★ Reuse is at SURVEY level, not question level. ``data.Vraag`` belongs to exactly one
   survey, and the numbers group by question id — so the same survey asked at ten events is one
   comparable series, while ten copies of a question are ten. That is why the roll-up across every
   event is the screen behind a row, and why the search is worth having: finding the set you
   already wrote beats writing it again.

   ★ Tags come out of the BU's EVENT category catalogue, the same chips the calendar filters
   on. One vocabulary, so "the questions we ask at townhalls" is a filter and not a naming
   convention. */

const emptyField = () => ({ label: '', type: 'shorttext', required: false, options: '',
                            correct: '', points: '' });

const CHOICE_TYPES = ['singleselect', 'multiselect'];

/* ★ The two values a yes/no is actually STORED as, in both renderers (Questions.js for a
   participant, PublicForm.js for the public link): the English word, lower-cased, never the
   translated label. A correct answer of "Ja" could never match one. */
const YESNO_VALUES = ['yes', 'no'];

/* Half a quiz question cannot be saved, and the server says so too: points with nothing to
   compare against are never awarded, and a right answer worth nothing is a fact no screen reads.
   Checked here as well so the answer is a disabled button and a line of explanation rather than a
   round trip that comes back as "could not save". */
const scoringIncomplete = (f) => !!(f.correct || '').trim() !== (Number(f.points) > 0);

/* The quiz half of a question: what counts as right, and what it is worth.

   ★ Only for the types the SERVER says can be scored (`scorable_types`) — an essay, a date
   or an email has no single right answer, and a multiselect stores its picks joined into one
   string, so "correct" would depend on the order they were ticked. The list travels in the payload
   instead of being written out a second time here, where it could drift from what the builder
   accepts. */
const Scoring = ({ field, types, onChange, t }) => {
  if (!(types || []).includes(field.type)) return null;
  const options = (field.options || '').split('\n').map((o) => o.trim()).filter(Boolean);
  const picks = field.type === 'yesno'
    ? YESNO_VALUES
    : (field.type === 'singleselect' ? options : null);
  const label = (o) => (o === 'yes' ? t('yes') : (o === 'no' ? t('no') : o));
  return (
    <>
      <span className="bravos-q__hint">{t('quizNote')}</span>
      {/* Picked, never typed, wherever the answers are a known list: a correct answer that is not
          on the list can never be given, so typing one is a typo every time. */}
      {picks ? (
        <select
          value={field.correct || ''}
          onChange={(e) => onChange({ ...field, correct: e.target.value })}
        >
          <option value="">{t('noCorrectAnswer')}</option>
          {picks.map((o) => <option key={o} value={o}>{label(o)}</option>)}
        </select>
      ) : (
        <input
          type={field.type === 'shorttext' ? 'text' : 'number'}
          value={field.correct || ''}
          maxLength={255}
          placeholder={t('correctAnswer')}
          onChange={(e) => onChange({ ...field, correct: e.target.value })}
        />
      )}
      <input
        type="number"
        min="0"
        max="1000"
        value={field.points === 0 ? '' : (field.points || '')}
        placeholder={t('pointsPlaceholder')}
        onChange={(e) => onChange({ ...field, points: e.target.value })}
      />
      {scoringIncomplete(field) && <span className="ce-note">{t('quizNeedsBoth')}</span>}
    </>
  );
};

const emptySurvey = () => ({ name: '' });

/* The three ways a set can stand to the diary. Derived from how many events it runs on
   rather than stored, so it can never disagree with the links themselves. */
const USES = ['reused', 'event', 'unused'];

/* And the three ways it stands to the CLOCK: taking answers right now, opening later, or over.
   Unlike the phases, this vocabulary is not sent — the counts arrive keyed by it, and the order
   is a reading decision rather than a server one: live first, because "which of these are running
   today" is the question the chips exist to answer. */
const WINDOWS = ['open', 'scheduled', 'closed'];

/* One event as the pickers print it: a programme is a container ("Culture year 2027"), the
   evenings under it are the events, and both can carry a survey. */
const eventLabel = (row, lang) => `${row.name}${row.date ? ` · ${shortDate(row.date, lang)}` : ''}`;

const CompanySurveys = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  /* ★ Which survey is open is STATE, not a query param. `usePersistUrlParams` (mounted app-wide)
     stashes every query param in sessionStorage and then strips it from the URL a tick after it
     appears — so `?open=` set by a click was gone by the next render and the detail bounced
     straight back to the list. That was "the arrow does nothing".

     The URL still gets one job: the cold open from the host screen's "edit questions"
     (/company-surveys?open=<id>). It is read once here, at mount, before the hook has swept —
     and out of sessionStorage as well, for the render where it already has. Consumed, not kept:
     left there, the next visit to the library would open whatever was opened last. */
  const [openId, setOpenId] = useState(() => {
    const stashed = sessionStorage.getItem('open');
    if (stashed) sessionStorage.removeItem('open');
    return params.get('open') || stashed || null;
  });

  const [list, setList] = useState(null);
  /* ★ A Lab host reads this screen and writes into it, but may not start a set HERE. Their claim
     on a survey is the events it runs on, so one born loose would be theirs for no time at all —
     they write it from the evening, which attaches it in the same press. The server refuses it
     either way; this only stops the button offering something that cannot work. */
  const [canCreate, setCanCreate] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [tagCounts, setTagCounts] = useState({});
  const [useCounts, setUseCounts] = useState({});
  // Before / during / after, and how many sets play each role. The vocabulary comes from the
  // server (``phases``) rather than being written out here a second time — it is the same list
  // the phase dropdown on a survey offers, and two copies of it drift.
  const [phaseList, setPhaseList] = useState([]);
  const [phaseCounts, setPhaseCounts] = useState({});
  // Open now / opens later / closed, and how many sets are on each side of their own dates.
  const [windowCounts, setWindowCounts] = useState({});
  // What is typed, and what has been asked for. The server does the searching — it is the only
  // side that can see the QUESTION texts — so the typing is debounced into `query`.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState([]);
  const [use, setUse] = useState('');
  const [phase, setPhase] = useState('');
  // ★ NOT called `window`. This module calls window.confirm to delete a survey, and a state
  // variable of that name shadows the global for the whole component.
  const [windowState, setWindowState] = useState('');
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editField, setEditField] = useState(null);
  const [showResults, setShowResults] = useState(false);
  // The title while it is being retyped. null = "showing the name the server has".
  const [naming, setNaming] = useState(null);
  // The from–to while it is being typed. null = "showing what the server has".
  const [winDraft, setWinDraft] = useState(null);
  const [newSurvey, setNewSurvey] = useState(null);
  const [attachTo, setAttachTo] = useState('');
  // Two seconds of "Copied", then back. The button IS the feedback — a toast for a clipboard
  // write is a notification about something the person just did on purpose.
  const [copied, setCopied] = useState(false);

  const copyLink = (link) => {
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(done).catch(done);
    } else {
      // No clipboard API (an insecure origin, an old browser): the address is printed in full
      // above the button, so there is still something to select by hand.
      done();
    }
  };

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (query.trim()) qs.set('q', query.trim());
    if (use) qs.set('use', use);
    if (phase) qs.set('phase', phase);
    if (windowState) qs.set('window', windowState);
    picked.forEach((id) => qs.append('tag', id));
    const suffix = qs.toString();
    apiRequest('GET', `/company/surveys${suffix ? `?${suffix}` : ''}`)
      .then((res) => {
        setList(res.surveys || []);
        setCatalog(res.tags || []);
        setCanCreate(res.can_create !== false);
        // ★ Counts come back computed over the WHOLE library, before the filters. A tally that
        // shrinks as you click reads as "there are fewer of these now".
        setTagCounts(res.tag_counts || {});
        setUseCounts(res.use_counts || {});
        setPhaseList(res.phases || []);
        setPhaseCounts(res.phase_counts || {});
        setWindowCounts(res.window_counts || {});
      })
      .catch(() => navigate('/member-home'));
  }, [navigate, query, use, phase, windowState, picked]);

  useEffect(() => { load(); }, [load]);

  // Typing searches the question texts, so every keystroke would be a query. A short wait is the
  // difference between a search box and a load test.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const toggleTag = (id) => setPicked((prev) => (prev.includes(id)
    ? prev.filter((t2) => t2 !== id)
    : [...prev, id]));

  const loadDetail = useCallback(() => {
    if (!openId) { setDetail(null); return; }
    apiRequest('GET', `/company/survey/${openId}`)
      .then(setDetail)
      .catch(() => { setDetail(null); setOpenId(null); });
    // params/setParams are stable enough here and adding them re-runs the fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const act = (promise, after) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    promise
      .then((res) => { load(); loadDetail(); if (after) after(res || {}); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const saveField = (field, fieldId) => {
    const body = {
      label: field.label,
      type: field.type,
      required: !!field.required,
      options: CHOICE_TYPES.includes(field.type)
        ? field.options.split('\n').map((o) => o.trim()).filter(Boolean)
        : [],
    };
    // Sent only for the types that can carry it — which is also how the pair gets cleared when
    // a quiz question is retyped into something nothing can be right about.
    if (((detail && detail.scorable_types) || []).includes(field.type)) {
      body.points = Number(field.points) || 0;
      body.correct_answer = (field.correct || '').trim();
    }
    act(fieldId
      ? apiRequest('PUT', `/company/survey/${openId}/fields/${fieldId}`, body)
      : apiRequest('POST', `/company/survey/${openId}/fields`, body));
  };

  /* --- one survey ------------------------------------------------------------------------ */

  if (openId && detail) {
    const survey = detail.survey || {};
    const fields = detail.fields || [];
    const results = detail.results || {};
    const byEvent = results.by_event || [];
    const linked = detail.events || [];
    const available = detail.available || [];
    const phases = detail.phases || [];
    const saved = { start_date: survey.start_date || '', end_date: survey.end_date || '' };
    const win = winDraft || saved;
    const winChanged = win.start_date !== saved.start_date || win.end_date !== saved.end_date;
    // Every link can go now — the library owns the survey, not the events. A legacy binding
    // is the survey's own home rather than a link, so that one still cannot be unlinked.
    const surveyTags = detail.tags || [];

    return (
      <Base activeNav="home">
        <Card>
          <Back onBack={() => { setNaming(null); setOpenId(null); }} />
          {/* The title, and a way to change it. A set of questions is named before it is
              written, so the name is the thing most likely to be wrong — "Survey 2" until you
              know what it turned into. Renaming is safe at any time: the answers hang off the
              QUESTIONS, and nothing anywhere is looked up by this string. */}
          {naming === null ? (
            <Title my="1" className="mt-3">
              <Icon name="document" theme="primary" className="me-2" />
              {survey.name}
            </Title>
          ) : (
            <div className="bravos-form mt-3">
              <input
                type="text"
                value={naming}
                maxLength={100}
                placeholder={t('newSurveyPlaceholder')}
                onChange={(e) => setNaming(e.target.value)}
              />
              <div className="bravos-form__row">
                <button
                  type="button"
                  className="bravos-submit"
                  disabled={busy || !naming.trim()}
                  onClick={() => { const name = naming.trim(); setNaming(null);
                    act(apiRequest('PUT', `/company/survey/${openId}`, { name })); }}
                >
                  {t('save')}
                </button>
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        onClick={() => setNaming(null)}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}
          <div className="ce-row__top">
            <span className={`ce-pill ${survey.is_published ? 'ce-pill--going' : 'ce-pill--draft'}`}>
              {survey.is_published ? t('surveyPublished') : t('notPublished')}
            </span>
            <span className={`ce-pill ${survey.is_open ? 'ce-pill--going' : 'ce-pill--draft'}`}>
              {survey.is_open ? t('surveyOpen') : t('surveyClosed')}
            </span>
            {/* A set with scored questions is a quiz, and the only thing worth saying about it
                on the header is what the whole thing is worth. */}
            {survey.points > 0 && (
              <span className="ce-pill ce-pill--going">
                {t('quizWorth', { n: survey.points })}
              </span>
            )}
          </div>
          {/* Publishing is what mints the public page a reminder mail can link to. Until it
              happens there is nowhere to send anyone, which is why the button is loud. */}
          {!survey.is_published && (
            <button
              type="button"
              className="bravos-submit"
              disabled={busy || fields.length === 0}
              onClick={() => act(apiRequest('PUT', `/company/survey/${openId}`,
                                            { publish: true }))}
            >
              {t('publishSurvey')}
            </button>
          )}
          {!survey.is_published && fields.length === 0 && (
            <p className="ce-note">{t('addAQuestionFirst')}</p>
          )}
          {naming === null && (
            <button
              type="button"
              className="ce-person__act"
              onClick={() => setNaming(survey.name || '')}
            >
              {t('renameSurvey')}
            </button>
          )}
          {notice && <p className="bravos-cal__notice">{notice}</p>}
        </Card>

        {/* --- the link ---------------------------------------------------------------------- */}
        {/* What publishing actually MINTS, printed. The whole point of a public page is that it
            can be sent to somebody, and this screen would make one and then never say where it
            was — the address only appeared on the host screen, attached to one evening, so a set
            that runs on no event at all had a page nobody could reach.

            ★ Shown in full and not only behind a button. A clipboard write is invisible, blocked
            on an insecure origin, and impossible to check; an address you can read is one you can
            select by hand, paste into a mail you are writing anyway, or verify before you send it
            to four hundred people.

            ★ The hint is not decoration. This link carries no ``?event=``, so answers through it
            are not attributed to any evening — right for a poll, wrong for "how was last
            Thursday", and the host screen's own copy button is where that second one lives.

            ★ The two arms are on `link` and not on `is_published`, so there is no third state
            that draws nothing: publishing is what mints the slug, and a set that somehow had one
            without the other would still say something rather than leaving a hole where every
            other survey has a card. */}
        <Card>
          <Title my="1">{t('surveyLink')}</Title>
          {survey.link ? (
            <>
              <p className="bravos-q__hint mb-2">{t('surveyLinkNote')}</p>
              <p className="sl-link">{survey.link}</p>
              <button
                type="button"
                className="bravos-copy"
                onClick={() => copyLink(survey.link)}
              >
                <Icon name={copied ? 'check' : 'link'} />
                {copied ? t('copied') : t('copyLink')}
              </button>
            </>
          ) : (
            /* Say where the address WILL be. The publish button is one card up. */
            <p className="ce-note">{t('surveyLinkUnpublished')}</p>
          )}
        </Card>

        {/* --- the window ------------------------------------------------------------------- */}
        <Card>
          <Title my="1">{t('surveyWindow')}</Title>
          <p className="bravos-q__hint mb-2">{t('surveyWindowNote')}</p>
          <div className="bravos-form">
            {/* From and to, on one line. They are the two ends of a single thing, and stacked
                they read as two separate settings that happen to be near each other. */}
            <div className="bravos-form__row">
              <label className="ce-check ce-check--stack">
                {t('surveyOpens')}
                <input
                  type="datetime-local"
                  value={win.start_date}
                  onChange={(e) => setWinDraft({ ...win, start_date: e.target.value })}
                />
              </label>
              <label className="ce-check ce-check--stack">
                {t('surveyCloses')}
                <input
                  type="datetime-local"
                  value={win.end_date}
                  onChange={(e) => setWinDraft({ ...win, end_date: e.target.value })}
                />
              </label>
            </div>
            {winChanged && (
              <div className="bravos-form__row">
                <button
                  type="button"
                  className="bravos-submit"
                  disabled={busy}
                  onClick={() => act(
                    apiRequest('PUT', `/company/survey/${openId}`,
                               { start_date: win.start_date, end_date: win.end_date }),
                    () => setWinDraft(null))}
                >
                  {t('save')}
                </button>
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        onClick={() => setWinDraft(null)}>
                  {t('cancel')}
                </button>
              </div>
            )}
          </div>
        </Card>

        {/* --- what kind of thing it is ------------------------------------------------------ */}
        <Card>
          <Title my="1">{t('surveyTags')}</Title>
          <p className="bravos-q__hint mb-2">{t('surveyTagsNote')}</p>
          <TagChips
            tags={detail.tag_catalog || []}
            selected={surveyTags.map((tag) => tag.id)}
            disabled={busy}
            onToggle={(id) => {
              const on = surveyTags.some((tag) => tag.id === id);
              const next = on ? surveyTags.filter((tag) => tag.id !== id).map((tag) => tag.id)
                              : [...surveyTags.map((tag) => tag.id), id];
              act(apiRequest('PUT', `/company/survey/${openId}/tags`, { tags: next }));
            }}
          />
        </Card>

        {/* --- what it asks ----------------------------------------------------------------- */}
        <Card className="sl-questions">
          <Title my="1">{t('surveyQuestions')}</Title>
          {fields.length === 0 && <p className="ce-note">{t('noQuestionsYet')}</p>}

          {fields.map((f) => (editField && editField.id === f.id ? (
            <div key={f.id} className="bravos-form">
              <input
                type="text"
                value={editField.label}
                maxLength={255}
                onChange={(e) => setEditField({ ...editField, label: e.target.value })}
              />
              <select
                value={editField.type}
                onChange={(e) => setEditField({ ...editField, type: e.target.value })}
              >
                {(detail.field_types || []).map((ft) => (
                  <option key={ft} value={ft}>{t(`ftype_${ft}`)}</option>
                ))}
              </select>
              {CHOICE_TYPES.includes(editField.type) && (
                <textarea
                  rows={3}
                  value={editField.options}
                  placeholder={t('optionsPlaceholder')}
                  onChange={(e) => setEditField({ ...editField, options: e.target.value })}
                />
              )}
              <label className="ce-check">
                <input
                  type="checkbox"
                  checked={!!editField.required}
                  onChange={(e) => setEditField({ ...editField, required: e.target.checked })}
                />
                {t('fieldRequired')}
              </label>
              <Scoring field={editField} types={detail.scorable_types}
                       onChange={setEditField} t={t} />
              <div className="bravos-form__row">
                <button
                  type="button"
                  className="bravos-submit"
                  disabled={busy || !editField.label.trim() || scoringIncomplete(editField)}
                  onClick={() => { const f2 = editField; setEditField(null); saveField(f2, f2.id); }}
                >
                  {t('save')}
                </button>
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        onClick={() => setEditField(null)}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div key={f.id} className="ce-person">
              <span className="ce-person__body">
                <span className="ce-person__name">
                  {f.label}{f.required ? ' *' : ''}
                </span>
                <span className="ce-person__email">
                  {t(`ftype_${f.type}`)}
                  {f.options.length > 0 ? ` · ${f.options.join(', ')}` : ''}
                  {f.points > 0 ? ` · ${t('rightAnswerWorth',
                                          { answer: f.correct_answer, n: f.points })}` : ''}
                </span>
              </span>
              <button
                type="button"
                className="ce-person__act"
                onClick={() => setEditField({ ...f, options: (f.options || []).join('\n'),
                                              correct: f.correct_answer || '',
                                              points: f.points || '' })}
              >
                {t('edit')}
              </button>
              <button
                type="button"
                className="ce-person__act"
                disabled={busy}
                onClick={() => act(apiRequest('DELETE',
                  `/company/survey/${openId}/fields/${f.id}`))}
              >
                {t('delete')}
              </button>
            </div>
          )))}

          {draft ? (
            <div className="bravos-form">
              <input
                type="text"
                value={draft.label}
                maxLength={255}
                placeholder={t('questionPlaceholder')}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
              <select
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value })}
              >
                {(detail.field_types || []).map((ft) => (
                  <option key={ft} value={ft}>{t(`ftype_${ft}`)}</option>
                ))}
              </select>
              {CHOICE_TYPES.includes(draft.type) && (
                <textarea
                  rows={3}
                  value={draft.options}
                  placeholder={t('optionsPlaceholder')}
                  onChange={(e) => setDraft({ ...draft, options: e.target.value })}
                />
              )}
              <label className="ce-check">
                <input
                  type="checkbox"
                  checked={!!draft.required}
                  onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
                />
                {t('fieldRequired')}
              </label>
              <Scoring field={draft} types={detail.scorable_types} onChange={setDraft} t={t} />
              <div className="bravos-form__row">
                <button
                  type="button"
                  className="bravos-submit"
                  disabled={busy || !draft.label.trim() || scoringIncomplete(draft)}
                  onClick={() => { const f = draft; setDraft(null); saveField(f); }}
                >
                  {t('save')}
                </button>
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        onClick={() => setDraft(null)}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => setDraft(emptyField())}
            >
              <Icon name="plus" /> {t('addQuestion')}
            </button>
          )}
        </Card>

        {/* --- where it runs ---------------------------------------------------------------- */}
        <Card>
          <Title my="1">{t('runsOnEvents')}</Title>
          <p className="bravos-q__hint mb-2">{t('runsOnEventsNote')}</p>
          {linked.length === 0 && <p className="ce-note">{t('notUsedYet')}</p>}

          {linked.map((e) => (
            <div key={e.event_id} className="ce-person">
              <span className="ce-person__body">
                <span className="ce-person__name">{e.name}</span>
                <span className="ce-person__email">
                  {shortDate(e.date, i18n.language)}
                  {e.is_programme ? ` · ${t('programme')}` : ''}
                </span>
              </span>
              {/* The phase is the ROLE the survey plays at THIS event, which is why it lives on
                  the link: one satisfaction survey is the "after" of twenty evenings. */}
              <select
                value={e.phase || ''}
                disabled={busy || e.legacy}
                onChange={(ev) => act(apiRequest('POST', `/company/survey/${openId}/events`,
                                                 { event_id: e.event_id, phase: ev.target.value }))}
              >
                <option value="">{t('noPhase')}</option>
                {phases.map((p) => <option key={p} value={p}>{t(`phase_${p}`)}</option>)}
              </select>
              {!e.legacy && (
                <button
                  type="button"
                  className="ce-person__act"
                  disabled={busy}
                  onClick={() => act(apiRequest('DELETE',
                    `/company/survey/${openId}/events/${e.event_id}`))}
                >
                  {t('remove')}
                </button>
              )}
            </div>
          ))}

          {available.length > 0 && (
            <div className="bravos-form__row">
              <select value={attachTo} onChange={(e) => setAttachTo(e.target.value)}>
                <option value="">{t('addToAnEvent')}</option>
                {available.map((e) => (
                  <option key={e.event_id} value={e.event_id}>
                    {eventLabel(e, i18n.language)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="bravos-submit"
                disabled={busy || !attachTo}
                onClick={() => { const id = attachTo; setAttachTo('');
                  act(apiRequest('POST', `/company/survey/${openId}/events`,
                                 { event_id: id })); }}
              >
                {t('add')}
              </button>
            </div>
          )}
        </Card>

        {/* --- on the member home ----------------------------------------------------------- */}
        {/* The one place a set of questions reaches people with no evening behind it. A poll is
            not an event feature: "which Friday suits everybody" belongs to the company, and its
            people are already on their home screen. It stays a normal set of questions — same
            window, same tags, same roll-up — so a set can stand here AND run on events. */}
        <Card>
          <Title my="1">{t('surveyOnHome')}</Title>
          <p className="bravos-q__hint mb-2">{t('surveyOnHomeNote')}</p>
          <label className="ce-check">
            <input
              type="checkbox"
              checked={!!survey.show_on_home}
              disabled={busy}
              onChange={(e) => act(apiRequest('PUT', `/company/survey/${openId}`,
                                              { show_on_home: e.target.checked }))}
            />
            {t('showOnMemberHome')}
          </label>
          {/* Two reasons it can be ticked and still invisible, and both are worth saying out
              loud rather than leaving somebody to wonder why nobody answered: there is no page
              to answer on until it is published, and a closed window is a closed window. */}
          {survey.show_on_home && !survey.is_published && (
            <p className="ce-note">{t('publishSurveyFirst')}</p>
          )}
          {survey.show_on_home && survey.is_published && !survey.is_open && (
            <p className="ce-note">{t('onHomeButClosed')}</p>
          )}
        </Card>

        <Card>
          <Title my="1">{t('acrossAllEvents')}</Title>
          <p className="hv-total">{t('surveyResponses', { n: results.responses || 0 })}</p>

          {/* The breakdown IS the point: one row per event, so a single bad evening stops being
              invisible inside the average. */}
          {byEvent.map((row) => (
            <div key={row.event_id || 'none'} className="ce-person">
              <span className="ce-person__body">
                <span className="ce-person__name">{row.name || t('unattributedAnswers')}</span>
                <span className="ce-person__email">{shortDate(row.date, i18n.language)}</span>
              </span>
              <span className="ce-tab__n">{row.responses}</span>
            </div>
          ))}
          {byEvent.length === 0 && <p className="ce-note">{t('noAnswersYet')}</p>}

          {results.responses > 0 && (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => setShowResults((v) => !v)}
            >
              {showResults ? t('hideResults') : t('viewResults')}
            </button>
          )}
          {/* The host screen's renderer, not a second copy of it: the same question read here
              across every event and there for one evening has to look like the same question. */}
          {showResults && (results.questions || []).map((q) => (
            <QuestionResult key={q.id} q={q} t={t} />
          ))}
        </Card>

        <Card>
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            disabled={busy}
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (!window.confirm(t('deleteSurveyConfirm'))) return;
              act(apiRequest('DELETE', `/company/survey/${openId}`),
                  () => setOpenId(null));
            }}
          >
            <Icon name="cross-circle" /> {t('deleteSurvey')}
          </button>
        </Card>
      </Base>
    );
  }

  /* --- the library ----------------------------------------------------------------------- */

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="document" theme="primary" className="me-2" />
          {t('ourSurveys')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('ourSurveysNote')}</p>

        {/* Search first, because it is the one that finds a question you half remember — it
            matches the QUESTION texts, not just the titles. */}
        <div className="bravos-form">
          <input
            type="search"
            value={search}
            maxLength={80}
            placeholder={t('searchSurveys')}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Reused / one-off / not in use. Derived from the links, so it is always true, and it is
            how an event-specific set stays visible instead of being a second kind of thing. */}
        <div className="ce-tabs">
          <button
            type="button"
            className={`ce-tab ${use === '' ? 'is-on' : ''}`}
            onClick={() => setUse('')}
          >
            {t('allSurveys')}
          </button>
          {USES.map((u) => (
            <button
              key={u}
              type="button"
              className={`ce-tab ${use === u ? 'is-on' : ''}`}
              onClick={() => setUse(use === u ? '' : u)}
            >
              {t(`use_${u}`)} <span className="ce-tab__n">{useCounts[u] || 0}</span>
            </button>
          ))}
        </div>

        {/* Which of these are LIVE. A library of dated things gets asked that more than anything
            else, and answering it used to mean opening rows one at a time and reading two
            datetimes off each.

            ★ The three states come off the survey's own from–to, exactly as the "open / closed"
            pill on a row does — so they partition the library and a set with no window at all
            sits under "open now", because an absent date means "no limit" everywhere else here.

            ★ A date filter, and only that: a set can be inside its window and still have no page
            to answer on. The "not published" pill on the row is the other half of that sentence
            and stays where it is. */}
        <div className="ce-tabs">
          <button
            type="button"
            className={`ce-tab ${windowState === '' ? 'is-on' : ''}`}
            onClick={() => setWindowState('')}
          >
            {t('anyDate')}
          </button>
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className={`ce-tab ${windowState === w ? 'is-on' : ''}`}
              onClick={() => setWindowState(windowState === w ? '' : w)}
            >
              {t(`window_${w}`)} <span className="ce-tab__n">{windowCounts[w] || 0}</span>
            </button>
          ))}
        </div>

        {/* Before / during / after — the moment the questions are asked.

            ★ The phase is a property of the LINK, not of the survey, so this narrows to "asked
            at that moment on at least one event". A set that is the pre-survey of one evening and
            the post-survey of the next appears under both, and the three counts deliberately add
            up to more than the library: the same questions really are asked at two moments. */}
        <div className="ce-tabs">
          <button
            type="button"
            className={`ce-tab ${phase === '' ? 'is-on' : ''}`}
            onClick={() => setPhase('')}
          >
            {t('anyPhase')}
          </button>
          {phaseList.map((p) => (
            <button
              key={p}
              type="button"
              className={`ce-tab ${phase === p ? 'is-on' : ''}`}
              onClick={() => setPhase(phase === p ? '' : p)}
            >
              {t(`phase_${p}`)} <span className="ce-tab__n">{phaseCounts[p] || 0}</span>
            </button>
          ))}
        </div>

        {catalog.length > 0 && (
          <div className="ce-tagrow">
            <TagChips tags={catalog} selected={picked} onToggle={toggleTag} counts={tagCounts} />
          </div>
        )}

        {list === null ? (
          <span className="auth__spinner" />
        ) : list.length === 0 ? (
          <p className="ce-note">
            {query || picked.length > 0 || use || phase || windowState
              ? t('noSurveysMatch') : t('noSurveysLibrary')}
          </p>
        ) : (
          list.map((s) => (
            <div
              key={s.id}
              className="ce-row"
              role="button"
              tabIndex={0}
              onClick={() => setOpenId(s.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') setOpenId(s.id); }}
            >
              <span className="ce-date">
                <span className="ce-date__day">{s.responses}</span>
                <span className="ce-date__mon">{t('answersShort')}</span>
              </span>
              <span className="ce-row__body">
                <span className="ce-row__top">
                  <span className="ce-row__name">{s.name}</span>
                  {!s.is_published && (
                    <span className="ce-pill ce-pill--draft">{t('notPublished')}</span>
                  )}
                  {/* Its dates, but only when they say something. "Open now" is what most of a
                      library is, and labelling the normal case is what makes a list unreadable —
                      the counts on the chips above already state it. A set that has not started
                      or has finished is the row you were looking for. */}
                  {s.window && s.window !== 'open' && (
                    <span className="ce-pill ce-pill--draft">{t(`window_${s.window}`)}</span>
                  )}
                  {s.points > 0 && (
                    <span className="ce-pill ce-pill--going">
                      {t('quizWorth', { n: s.points })}
                    </span>
                  )}
                  {/* A poll does not show up in the "reused / one event / not in use" line
                      below — that counts EVENTS, and a set asked on the home has none. Without
                      this pill the busiest question set on the screen reads as "not in use". */}
                  {s.show_on_home && (
                    <span className="ce-pill ce-pill--online">{t('onMemberHome')}</span>
                  )}
                </span>
                <span className="ce-row__meta">
                  {t(`use_${s.use}`)} · {t('onNEvents', { n: s.events })}
                  {' · '}{t('nQuestions', { n: s.fields })}
                  {/* Which moments it is asked at, so a row makes it plain why it matched the
                      phase filter — and, on an unfiltered list, that a set plays two roles. */}
                  {(s.phases || []).length > 0
                    && ` · ${(s.phases || []).map((p) => t(`phase_${p}`)).join(', ')}`}
                </span>
                <TagPills tags={s.tags} />
              </span>
              <Icon name="angle-right" className="bravos-event__chev" />
            </div>
          ))
        )}

        {/* A name and nothing else. A question set exists before the evening that asks it —
            that is what a library is — so where it runs is a later decision, taken here or on
            the event itself. */}
        {!canCreate ? null : newSurvey ? (
          <div className="bravos-form">
            <input
              type="text"
              value={newSurvey.name}
              maxLength={100}
              placeholder={t('newSurveyPlaceholder')}
              onChange={(e) => setNewSurvey({ ...newSurvey, name: e.target.value })}
            />
            <div className="bravos-form__row">
              <button
                type="button"
                className="bravos-submit"
                disabled={busy || !newSurvey.name.trim()}
                onClick={() => { const body = { ...newSurvey, name: newSurvey.name.trim() };
                  setNewSurvey(null);
                  act(apiRequest('POST', '/company/surveys', body),
                      (res) => setOpenId(res.survey_id)); }}
              >
                {t('save')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setNewSurvey(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            onClick={() => setNewSurvey(emptySurvey())}
          >
            <Icon name="plus" /> {t('newSurvey')}
          </button>
        )}
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>
    </Base>
  );
};

export default CompanySurveys;
